/**
 * Iter55 — Shared media helpers
 * ────────────────────────────────────────────────────────────────────────────
 * Reusable primitives extracted from HeroCarousel so any homepage/plan
 * module can consume the same URL parsers, CTA resolver, scheduling filter
 * and unified analytics tracker.
 */
import api from "@/lib/api";

// ── URL parsers ──
const YT_RE = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/;
const VIMEO_RE = /vimeo\.com\/(?:video\/)?([0-9]+)/;

export const youtubeEmbed = (url) => {
  const m = String(url || "").match(YT_RE);
  if (!m) return null;
  const id = m[1];
  return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=1&modestbranding=1&rel=0&playsinline=1`;
};
export const vimeoEmbed = (url) => {
  const m = String(url || "").match(VIMEO_RE);
  if (!m) return null;
  return `https://player.vimeo.com/video/${m[1]}?autoplay=1&muted=1&loop=1`;
};

// ── Scheduling filter ──
// An item is live if enabled AND (no publish_at OR now >= publish_at) AND
// (no expires_at OR now <= expires_at). Reused for hero slides + experiences.
export const isItemLive = (item, now = new Date()) => {
  if (!item) return false;
  if (item.enabled === false) return false;
  const start = item.publish_at ? new Date(item.publish_at) : null;
  const end = item.expires_at ? new Date(item.expires_at) : null;
  if (start && !Number.isNaN(start.getTime()) && now < start) return false;
  if (end && !Number.isNaN(end.getTime()) && now > end) return false;
  return true;
};

// ── CTA resolver ──
export const resolveCtaHref = (cta) => {
  if (!cta) return null;
  if (typeof cta === "string") return cta;
  const value = (cta.href || cta.value || "").trim();
  if (!value) return null;
  switch (cta.type) {
    case "plan":       return value.startsWith("/") ? value : `/plans/${value}`;
    case "collection": return value.startsWith("/") ? value : `/plan-collections/${value}`;
    case "category":   return value.startsWith("/") ? value : `/plans?style=${encodeURIComponent(value)}`;
    case "blog":       return value.startsWith("/") ? value : `/blog/${value}`;
    case "walkthrough":
    case "tour_360":
    case "matterport":
    case "coohom":     return value;
    case "contact":    return value.startsWith("/") ? value : "/contact";
    case "external":   return value;
    case "internal":
    default:           return value;
  }
};

// ── Unified analytics tracker ──
// One call to record an impression / click / cta_click against any tracked
// module (hero, experiences, …). Fire-and-forget so a failure never blocks
// the UX.
export const trackSectionEvent = (moduleName, itemId, event) => {
  if (!moduleName || !itemId || !event) return;
  try {
    api.post("/analytics/section-event", { module: moduleName, item_id: itemId, event }).catch(() => {});
  } catch (_e) { /* noop */ }
};
